import { useEffect, useState } from "react";
import { Link, Share2, Check, X, Copy, Trash2, RefreshCw } from "lucide-react";
import client, { errText } from "../lib/api";

/**
 * Owner-only Shares screen. Lists the person's shares of the
 * one-pager and lets them create a new one, copy the link, or
 * revoke an existing one. No auto-email is ever sent to any
 * recipient; the link is the person's to share.
 */
export default function SharesScreen() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const [justCreated, setJustCreated] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await client.get("/resume/shares");
      setItems(data?.items || []);
    } catch (e) {
      setError(errText(e, "Could not load your shares."));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createShare = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await client.post("/resume/shares", {
        label: label.trim() || null, product: "start",
      });
      setLabel("");
      setJustCreated(data);
      await load();
    } catch (e) {
      setError(errText(e, "Could not create a share."));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (slug) => {
    setBusy(true);
    setError("");
    try {
      await client.post(`/resume/shares/${slug}/revoke`);
      await load();
    } catch (e) {
      setError(errText(e, "Could not revoke that share."));
    } finally {
      setBusy(false);
    }
  };

  const publicUrlFor = (path) => `${window.location.origin}${path}`;

  const copyLink = async (path) => {
    try {
      await navigator.clipboard.writeText(publicUrlFor(path));
    } catch (_e) {
      /* ignore */
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 pt-8 pb-24">
      <div className="k-fade-in mb-6">
        <div className="k-label mb-2">shares</div>
        <h1
          className="klareet-serif mb-2"
          style={{ fontSize: "clamp(1.6rem, 4vw, 2.2rem)", lineHeight: 1.1 }}
          data-testid="shares-title"
        >
          Your one-pager, on a copy-able link.
        </h1>
        <p className="k-hero-sub">
          You share the link yourself. We never email a recipient on your behalf.
          Revoke any share and its link goes 404, same as if it had never existed.
        </p>
      </div>

      {/* Create a share */}
      <div className="k-card mb-6" data-testid="shares-create-card">
        <div className="k-label mb-2 flex items-center gap-2">
          <Share2 size={14} /> a new share link
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            className="k-input flex-1"
            type="text"
            maxLength={80}
            placeholder="a label for you, e.g. 'for acme-inc'"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            data-testid="shares-label-input"
          />
          <button
            type="button"
            className="k-btn k-btn-primary"
            onClick={createShare}
            disabled={busy}
            data-testid="shares-create-btn"
          >
            <Link size={14} /> Create share link
          </button>
        </div>
        {justCreated && (
          <div
            className="mt-4 rounded-lg p-3"
            style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }}
            data-testid="shares-created-callout"
          >
            <div className="flex items-center gap-2 mb-1">
              <Check size={14} style={{ color: "hsl(var(--klareet-teal))" }} />
              <span className="text-sm font-medium" style={{ color: "hsl(var(--klareet-ink))" }}>
                Link ready. Copy and paste it wherever you like.
              </span>
            </div>
            <div className="klareet-mono text-xs break-all" data-testid="shares-created-url" style={{ color: "hsl(var(--klareet-ink-soft))" }}>
              {publicUrlFor(justCreated.path)}
            </div>
            <button
              type="button"
              className="k-btn-outline mt-2"
              onClick={() => copyLink(justCreated.path)}
              data-testid="shares-created-copy"
            >
              <Copy size={12} /> Copy link
            </button>
          </div>
        )}
        {error && (
          <div className="mt-3 text-sm" style={{ color: "hsl(0 55% 45%)" }} data-testid="shares-error">
            {error}
          </div>
        )}
      </div>

      {/* List */}
      <div className="k-card" data-testid="shares-list-card">
        <div className="flex items-center justify-between mb-3">
          <div className="k-label">your shares</div>
          <button type="button" className="k-btn-outline" onClick={load} disabled={busy} data-testid="shares-reload-btn">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        {items.length === 0 ? (
          <div className="k-quiet" data-testid="shares-empty">
            No shares yet. Create one above to hand someone a private view.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="shares-list">
            {items.map((s) => (
              <li
                key={s.slug}
                className="rounded-xl p-3 flex flex-col sm:flex-row gap-3 items-start sm:items-center"
                style={{
                  background: s.is_active
                    ? "hsl(var(--klareet-canvas-2))"
                    : "hsl(var(--klareet-canvas))",
                  border: "1px solid hsl(var(--klareet-line-soft))",
                  opacity: s.is_active ? 1 : 0.6,
                }}
                data-testid={`share-row-${s.slug}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium" style={{ color: "hsl(var(--klareet-ink))" }} data-testid={`share-label-${s.slug}`}>
                    {s.label || "no label"}
                  </div>
                  <div className="klareet-mono text-xs mt-1 break-all" style={{ color: "hsl(var(--klareet-ink-soft))" }} data-testid={`share-url-${s.slug}`}>
                    {publicUrlFor(s.path)}
                  </div>
                  <div className="k-quiet !text-xs mt-1" data-testid={`share-meta-${s.slug}`}>
                    {s.is_active ? "active" : `revoked ${new Date(s.revoked_at).toLocaleDateString()}`}
                    {" · "}
                    {s.view_count} {s.view_count === 1 ? "view" : "views"}
                  </div>
                </div>
                {s.is_active ? (
                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      className="k-btn-outline"
                      onClick={() => copyLink(s.path)}
                      data-testid={`share-copy-${s.slug}`}
                    >
                      <Copy size={12} /> Copy
                    </button>
                    <button
                      type="button"
                      className="k-btn-outline"
                      onClick={() => revoke(s.slug)}
                      disabled={busy}
                      data-testid={`share-revoke-${s.slug}`}
                    >
                      <Trash2 size={12} /> Revoke
                    </button>
                  </div>
                ) : (
                  <span
                    className="k-chip shrink-0"
                    style={{ background: "hsl(var(--klareet-canvas))", color: "hsl(var(--klareet-muted))" }}
                    data-testid={`share-revoked-${s.slug}`}
                  >
                    <X size={12} /> revoked
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
